import { useEffect, useState } from 'react';

export default function QuestionFormModal({ question, isNew, onSave, onClose }) {
  const [draft, setDraft] = useState({
    id: question?.id || `q-${Date.now()}`,
    text: question?.questionText || question?.text || '',
    type: question?.type || 'mcq',
    imageUrl: question?.imageUrl || '',
    options: question?.options || (question?.type === 'true-false' ? ['True', 'False'] : question?.type === 'task' ? ['Submit Solution & Image'] : ['', '', '', '']),
    correctAnswer: question?.correctAnswer ?? 0,
    explanation: question?.explanation || '',
  });

  // Sync when question prop changes (important for editing)
  useEffect(() => {
    if (question) {
      setDraft({
        id: question.id || `q-${Date.now()}`,
        text: question.questionText || question.text || '',
        type: question.type || 'mcq',
        imageUrl: question.imageUrl || '',
        options: question.options || (question.type === 'true-false' ? ['True', 'False'] : question.type === 'task' ? ['Submit Solution & Image'] : ['', '', '', '']),
        correctAnswer: question.correctAnswer ?? 0,
        explanation: question.explanation || '',
      });
    }
  }, [question]);

  const handleTypeChange = (type) => {
    let options = ['', '', '', ''];
    if (type === 'true-false') options = ['True', 'False'];
    if (type === 'task') options = ['Upload Project Screenshot / Solution'];

    setDraft(prev => ({ 
      ...prev, 
      type, 
      options, 
      correctAnswer: 0 
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be under 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setDraft(prev => ({ ...prev, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const updateOption = (idx, value) => {
    const options = [...draft.options];
    options[idx] = value;
    setDraft(prev => ({ ...prev, options }));
  };

  const handleSave = () => {
    if (!draft.text.trim()) return;

    // Convert to backend format
    const questionForBackend = {
      questionText: draft.text.trim(),
      type: draft.type,
      imageUrl: draft.imageUrl || '',
      options: draft.type === 'task' ? ['Task Completed & Submitted'] : draft.options,
      correctAnswer: draft.type === 'task' ? 0 : Number(draft.correctAnswer),
      explanation: draft.explanation.trim(),
    };

    onSave(questionForBackend);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">{isNew ? 'Add Question or Task' : 'Edit Question or Task'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer rounded-lg hover:bg-slate-100">
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Question Type */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-2">Item Type</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'mcq', label: 'Multiple Choice', icon: 'ri-list-check' },
                { id: 'true-false', label: 'True / False', icon: 'ri-toggle-line' },
                { id: 'task', label: 'Practical Task', icon: 'ri-task-line' }
              ].map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleTypeChange(type.id)}
                  className={`flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                    draft.type === type.id
                      ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                      : 'text-slate-600 border-slate-200 hover:border-teal-300 bg-white'
                  }`}
                >
                  <i className={`${type.icon} text-base`} />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Question / Task Prompt Text */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              {draft.type === 'task' ? 'Task / Practical Assignment Prompt *' : 'Question *'}
            </label>
            <textarea
              value={draft.text}
              onChange={e => setDraft(prev => ({ ...prev, text: e.target.value }))}
              placeholder={draft.type === 'task' ? 'e.g. Build a flexbox navbar with a CTA button and submit a screenshot of your output...' : 'Enter your question here...'}
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 resize-none"
            />
          </div>

          {/* Optional Reference Image for Question / Task */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              Reference Image / Diagram / Prompt Asset (Optional)
            </label>
            {draft.imageUrl ? (
              <div className="relative rounded-xl border border-slate-200 overflow-hidden mb-2 bg-slate-50">
                <img src={draft.imageUrl} alt="Reference" className="w-full h-36 object-contain" />
                <button
                  type="button"
                  onClick={() => setDraft(prev => ({ ...prev, imageUrl: '' }))}
                  className="absolute top-2 right-2 px-2.5 py-1 bg-rose-600 text-white text-xs font-semibold rounded-lg hover:bg-rose-700 shadow-md flex items-center gap-1"
                >
                  <i className="ri-delete-bin-line" /> Remove
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-slate-200 hover:border-teal-400 rounded-xl cursor-pointer bg-slate-50/50 hover:bg-teal-50/20 transition-all">
                  <i className="ri-image-add-line text-2xl text-teal-600 mb-1" />
                  <span className="text-xs font-medium text-slate-700">Click to upload reference image or diagram</span>
                  <span className="text-[11px] text-slate-400">PNG, JPG, WebP up to 5MB</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">or URL:</span>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={draft.imageUrl}
                    onChange={e => setDraft(prev => ({ ...prev, imageUrl: e.target.value }))}
                    className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Options for MCQ / True-False */}
          {draft.type !== 'task' ? (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-2">
                {draft.type === 'true-false' ? 'Options' : 'Answer Options *'}
              </label>
              <div className="space-y-2">
                {draft.options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => setDraft(prev => ({ ...prev, correctAnswer: idx }))}
                      className={`w-6 h-6 flex items-center justify-center rounded-full border-2 flex-shrink-0 cursor-pointer transition-colors ${
                        draft.correctAnswer === idx
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-slate-300 hover:border-teal-300'
                      }`}
                    >
                      {draft.correctAnswer === idx && <i className="ri-check-line text-white text-xs" />}
                    </button>
                    <input
                      type="text"
                      value={opt}
                      onChange={e => updateOption(idx, e.target.value)}
                      disabled={draft.type === 'true-false'}
                      placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                      className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none transition-colors ${
                        draft.type === 'true-false' 
                          ? 'bg-slate-50 text-slate-500 border-slate-200' 
                          : draft.correctAnswer === idx
                          ? 'border-teal-300 bg-teal-50 focus:border-teal-500'
                          : 'border-slate-200 focus:border-teal-500'
                      }`}
                    />
                    {draft.correctAnswer === idx && (
                      <span className="text-xs text-teal-600 font-semibold whitespace-nowrap">Correct</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Click the circle to mark the correct answer</p>
            </div>
          ) : (
            <div className="p-3.5 bg-teal-50/70 border border-teal-200 rounded-xl">
              <div className="flex items-center gap-2 text-teal-800 font-semibold text-xs mb-1">
                <i className="ri-information-line text-sm text-teal-600" />
                Practical Task Mode
              </div>
              <p className="text-xs text-teal-700 leading-relaxed">
                Students will be prompted to solve this task, write their notes/code, and upload a screenshot or image of their work to complete the lesson checkpoint.
              </p>
            </div>
          )}

          {/* Explanation */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              {draft.type === 'task' ? 'Solution Guidelines / Grading Tips (Optional)' : 'Explanation (Optional)'}
            </label>
            <textarea
              value={draft.explanation}
              onChange={e => setDraft(prev => ({ ...prev, explanation: e.target.value }))}
              placeholder={draft.type === 'task' ? 'Provide key points or criteria for a successful task solution...' : 'Explain why this is the correct answer...'}
              rows={2}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg cursor-pointer hover:bg-slate-100 whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!draft.text?.trim()}
            className="px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg cursor-pointer hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap shadow-sm"
          >
            {isNew ? 'Add Item' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
